import { useDebounce } from 'use-debounce';
import { useLocation } from '../context/LocationContext';
import { useCustomerRegistry, useCustomerRegistryEnabled } from '../hooks/useCustomerRegistry';
import type { SearchHit } from '@ordr-as/ordr-service-types/epos';
import InvoiceField from './InvoiceField';
import { useEffect, useState } from 'react';

const invoiceDynamicFields = [
  {
    displayLabel: 'Customer',
    name: 'externalReference',
    required: true,
    type: 'TEXT_COMBO_BOX',
  },
  {
    displayLabel: 'Reference Person',
    name: 'referencePerson',
    required: false,
    type: 'TEXT_SINGLE_LINE',
  },
  {
    displayLabel: 'Comment',
    name: 'comment',
    required: false,
    type: 'TEXT_SINGLE_LINE',
  },
];

interface InvoiceInfoContainerProps {
  invoiceFieldValues?: {
    customerRegistryId?: string | undefined;
    customerName?: string | undefined;
    referencePerson?: string | undefined;
    comment?: string | undefined;
  };
  setInvoiceFieldValues: React.Dispatch<
    React.SetStateAction<{
      customerRegistryId?: string | undefined;
      customerName?: string | undefined;
      referencePerson?: string | undefined;
      comment?: string | undefined;
    }>
  >;
}

const InvoiceInfoContainer = (props: InvoiceInfoContainerProps) => {
  const { invoiceFieldValues, setInvoiceFieldValues } = props;
  const { location } = useLocation();
  const { data: customerRegistryEnabledDto } = useCustomerRegistryEnabled(location.id);
  const [searchText, setSearchText] = useDebounce<string | undefined>(invoiceFieldValues?.customerName, 300);
  const { data: customerRegistry } = useCustomerRegistry(location.id, searchText);
  const [dynamicFieldValues, setDynamicFieldValues] = useState<Record<string, string>>({
    comment: invoiceFieldValues?.comment ?? '',
    externalReference: invoiceFieldValues?.customerRegistryId ?? invoiceFieldValues?.customerName ?? '',
    referencePerson: invoiceFieldValues?.referencePerson ?? '',
  });

  useEffect(() => {
    const { externalReference } = dynamicFieldValues;
    const customerRegistryId = customerRegistryEnabledDto?.enabled ? externalReference : undefined;
    const match = customerRegistry?.hits.find((hit: SearchHit) => hit.externalReference === externalReference);
    const customerName = customerRegistryEnabledDto?.enabled ? match?.name : externalReference;
    const { referencePerson } = dynamicFieldValues;
    const { comment } = dynamicFieldValues;
    setInvoiceFieldValues({ comment, customerName, customerRegistryId, referencePerson });
  }, [customerRegistry?.hits, customerRegistryEnabledDto?.enabled, dynamicFieldValues, setInvoiceFieldValues]);

  return (
    invoiceDynamicFields?.map((unintegratedField) => (
      <InvoiceField
        key={unintegratedField.displayLabel}
        unintegratedField={unintegratedField}
        dyamicFieldValues={dynamicFieldValues}
        setDynamicFieldValues={setDynamicFieldValues}
        customerRegistryEnabledDto={customerRegistryEnabledDto}
        customerRegistry={customerRegistry}
        setSearchText={setSearchText}
      />
    )) ?? null
  );
};

export default InvoiceInfoContainer;
